import { Formik, Form, Field } from 'formik'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'

import { useNavigate, Link } from '../navigation'
import Modal from '../components/Modal'
import { patent_family } from '../data'
import { useMessages } from '../Messages'
import { IconLoad } from '../components/icons'
import { epoLoad } from '../backend'
import { useExternal } from './'
import { useAgents } from '../agents/AgentsProvider'
import { competitorFamilyUrl } from './utils'

// The EPO family scrape returns a (patent-shaped, transient) portfolio. We reuse it to enumerate the
// family, then on import we save a competitor family and — for each selected member — re-run the proven
// single-member scrape (which persists inventors/applicants) and store everything in the competitor tables.

const booleanRender = (v: unknown, t: (s: string) => string) =>
    typeof v === 'boolean' ? t(v ? 'yes' : 'no') : ''

const fields: { field: string, render?: (v: unknown, t: (s: string) => string) => string }[] = [
    { field: 'internalReference' },
    { field: 'countryCode' },
    { field: 'title' },
    { field: 'applicationNumber' },
    { field: 'publicationNumber' },
    { field: 'patentNumber' },
    { field: 'numberClaims' },
    { field: 'expiryDate' },
    { field: 'firstFiling', render: booleanRender },
]

// Member biblio fields copied from a single-member EPO scrape into the new competitor member.
const memberBiblioFields = [
    'title', 'ipType', 'numberClaims', 'patentOfficeLink', 'validated',
    'applicationNumber', 'applicationDate', 'publicationNumber', 'publicationDate',
    'patentNumber', 'patentDate', 'expiryDate', 'pctRouteFiling', 'firstFiling', 'unitaryPatent',
]

export function EpoImportCompetitorFamily() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setErrorMessage } = useMessages()
    const { families, postFamily, postMember, postCompetitorAgentLink } = useExternal()
    const { refetchAgents } = useAgents()

    const existingFamilyReferences = new Set(families.map(f => f.internalReference))

    const [portfolio, setPortfolio] = useState<Record<string, any> | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(false)

    const family = portfolio?.families?.[0]
    const members: Record<string, any>[] = _(portfolio?.members ?? []).sortBy('internalReference').value()

    const initialValues = {
        members: _(members).map(m => [m.internalReference, true]).fromPairs().value(),
        internalReference: family?.internalReference ?? '',
        familyName: family?.familyName ?? '',
        summary: family?.summary ?? '',
    }

    async function importPortfolio(values: typeof initialValues) {
        setIsLoading(true)
        try {
            const selected = members.filter(m => values.members[m.internalReference] ?? false)
            const savedFamily: any = await postFamily({
                internalReference: values.internalReference.trim(),
                familyName: values.familyName,
                summary: values.summary,
            } as any)
            const competitorFamilyId = savedFamily?.competitorFamilyId

            for (const pm of selected) {
                const lookupNumber = pm.publicationNumber || pm.patentNumber || pm.applicationNumber
                // Re-scrape the single member to get persisted inventor/applicant agents
                const mem: Record<string, any> | undefined = lookupNumber
                    ? await epoLoad({ patentFamilyId: 0, publicationNumber: lookupNumber }).catch(() => undefined)
                    : undefined
                const biblio = { ...pm, ..._.pick(mem ?? {}, memberBiblioFields) }

                const saved: any = await postMember({
                    ..._.pick(biblio, memberBiblioFields),
                    internalReference: pm.internalReference,
                    countryCode: pm.countryCode,
                    ipType: biblio.ipType ?? 'patent',
                    competitorMemberStatus: 'relevant-application',
                    competitorFamilyId,
                } as any)
                const competitorMemberId = saved?.competitorMemberId

                const agentIds = (linkType: 'inventors' | 'applicants') =>
                    ((mem?.[linkType] ?? []) as { agentId: number }[]).map(a => a.agentId).filter(Boolean)
                const links = [
                    ...agentIds('inventors').map(agentId => ({ realm: '', competitorMemberId, agentId, linkType: 'inventor' })),
                    ...agentIds('applicants').map(agentId => ({ realm: '', competitorMemberId, agentId, linkType: 'applicant' })),
                ]
                if (links.length > 0) await postCompetitorAgentLink(links as any)
            }

            await refetchAgents()
            navigate(competitorFamilyUrl({ internalReference: values.internalReference.trim() }))
        } catch (err: any) {
            setErrorMessage(err.message)
        } finally {
            setIsLoading(false)
        }
    }

    const cellStyle = 'px-1 border border-pcx-200'
    const backupRender = (v: unknown) => (v ?? '') as string

    return <>
        {/* @ts-ignore */}
        <Helmet>
            <title>{t('import-family-from-epo')} | {t('external')} | Patent Cockpit</title>
        </Helmet>
        <div className="portfolio-menu">
            <h2>{t('import-family-from-epo')}</h2>
        </div>
        <div className="main-content">
            {portfolio === undefined
                ? <Modal>
                    <Formik
                        initialValues={{ publicationNumber: '', internalReference: '' }}
                        onSubmit={(values, { setStatus }) =>
                            epoLoad({ ...values, type: patent_family as 'patent-family' })
                                .then(setPortfolio)
                                .catch(err => {
                                    console.warn(err.message)
                                    setStatus(t('epo-nothing-found'))
                                })}
                        validate={values => {
                            const errors: Record<string, string> = {}
                            if (typeof values.internalReference !== 'string' || values.internalReference.trim().length === 0)
                                errors.internalReference = t('reference-required')
                            else if (existingFamilyReferences.has(values.internalReference.trim()))
                                errors.internalReference = t('reference-used')
                            return errors
                        }}
                    >{({ errors, touched, status }) =>
                        <Form>
                            <div className='flex flex-col gap-2 p-4 w-fit'>
                                <h3 className='mb-2'>{t('import-family-from-epo')}</h3>
                                <label className='w-full'>
                                    <span className='label mb-1'>{t('internalReference')}</span>
                                    <Field name="internalReference" className='form-input w-full' type="text" required />
                                    {errors.internalReference && touched.internalReference &&
                                        <div className='text-red-600 text-sm py-1'>{errors.internalReference}</div>}
                                </label>
                                <label className='w-fit mb-2'>
                                    <span className='label mb-1 pr-1'>{t('publicationNumber')} / {t('patentNumber')}</span>
                                    <Field name="publicationNumber" className='form-input w-full' type="text" required />
                                </label>
                                {status && <div className='text-red-600 border-l-2 border-red-500 text-sm p-2 w-full'>{status}</div>}
                            </div>
                            <div className='bg-pcx-200 flex flex-row-reverse gap-4 p-4'>
                                <button className='btn-primary' type="submit">{t('load-from-epo')}</button>
                                <Link to=".." className='btn-secondary'>{t('cancel')}</Link>
                            </div>
                        </Form>
                    }</Formik>
                </Modal>
                : family !== undefined
                    ? <Formik initialValues={initialValues} onSubmit={importPortfolio}>
                        <Form className='max-w-3xl'>
                            <div className='flex flex-col items-start sm:flex-row-reverse gap-2 sm:gap-4 pb-8'>
                                <button className='btn-primary' disabled={isLoading} type="submit">{isLoading ? <IconLoad /> : t('import')}</button>
                                <button className='btn-secondary' type="button" onClick={() => setPortfolio(undefined)}>{t('try-again')}</button>
                                <Link to=".." className='btn-secondary'>{t('cancel')}</Link>
                            </div>
                            <div className='max-w-lg pb-8 flex flex-col gap-2'>
                                <label className='w-full'>
                                    <div className='label mb-1'>{t('internalReference')}</div>
                                    <Field name="internalReference" className='form-input w-full' />
                                </label>
                                <label className='w-full'>
                                    <div className='label mb-1'>{t('familyName')}</div>
                                    <Field name="familyName" className='form-input w-full' />
                                </label>
                                <label className='w-full'>
                                    <div className='label mb-1'>{t('summary')}</div>
                                    <Field name="summary" as="textarea" className="form-textarea h-32 w-full" />
                                </label>
                            </div>
                            <h4>{t('family-members')}</h4>
                            <table className='mt-4 text-sm text-slate-600 whitespace-nowrap'>
                                <thead className='text-left font-normal'>
                                    <tr>
                                        <th className={cellStyle}>{t('select')}</th>
                                        {fields.map(({ field }) => <th key={field} className={cellStyle}>{t(field)}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member, i) =>
                                        <tr key={i}>
                                            <td className={cellStyle + ' text-center'}>
                                                <Field type="checkbox" name={`members.${member.internalReference}`} className='form-checkbox' />
                                            </td>
                                            {fields.map(({ field, render }) =>
                                                <td key={field} className={cellStyle}>{(render ?? backupRender)(member[field], t)}</td>)}
                                        </tr>)}
                                </tbody>
                            </table>
                        </Form>
                    </Formik>
                    : <div className='max-w-lg'>
                        <h3>{t('family-not-found')}</h3>
                        <button className='btn-primary' onClick={() => setPortfolio(undefined)}>{t('try-again')}</button>
                        <Link to=".." className='btn-secondary'>{t('cancel')}</Link>
                    </div>
            }
        </div>
    </>
}
